import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { widgetTypes } from '../util/constants';
import { setSelectedWidgetIds } from '../../shared/state/ducks/widgets';

type Widget = {
  id: string;
  config: {
    height: number;
    width: number;
    xLocation: number;
    yLocation: number;
    parentId?: string;
    currentTabIndex?: number;
    currentSlideIndex?: number;
    currentMenuItem?: number;
    visible: boolean;
  };
  tabIndex?: number;
};

/**
 * Custom hook to enable drag selection of widgets on a page.
 *
 * @param {Object} currentPage - The current page object containing widgets.
 * @returns {Object} - An object containing the container reference, selection state, and the SelectionBox component.
 */

const useDragSelection = currentPage => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectionBoxRef = useRef<HTMLDivElement>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const dispatch = useDispatch();

  const handleDragSelectionMouseDown = (e: MouseEvent) => {
    if (e.shiftKey && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setIsSelecting(true);
      setStartPoint({ x, y });
      setSelectionBox({ x, y, width: 0, height: 0 });
    }
  };

  const handleDragSelectionMouseMove = (e: MouseEvent) => {
    if (isSelecting && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const newWidth = x - startPoint.x;
      const newHeight = y - startPoint.y;
      setSelectionBox({
        x: Math.min(x, startPoint.x),
        y: Math.min(y, startPoint.y),
        width: Math.abs(newWidth),
        height: Math.abs(newHeight),
      });
    }
  };

  const handleDragSelectionMouseUp = () => {
    if (isSelecting) {
      const selectedWidgets: string[] = [];
      const widgets: Widget[] = Object.values(currentPage.content) as Widget[];

      widgets.forEach(widget => {
        const isInSelectionBox =
          widget.config.xLocation < selectionBox.x + selectionBox.width &&
          widget.config.xLocation + widget.config.width > selectionBox.x &&
          widget.config.yLocation < selectionBox.y + selectionBox.height &&
          widget.config.yLocation + widget.config.height > selectionBox.y;

        if (isInSelectionBox) {
          const parentWidget = currentPage.content[widget.config.parentId];
          if (
            parentWidget &&
            (parentWidget.type === widgetTypes.Tabs ||
              parentWidget.type === widgetTypes.AccordionWidget ||
              parentWidget.type === widgetTypes.Slideshow ||
              parentWidget.type === widgetTypes.Navigation)
          ) {
            if (
              (widget.tabIndex === parentWidget.config.currentTabIndex ||
                widget.tabIndex === parentWidget.config.currentSlideIndex ||
                widget.tabIndex === parentWidget.config.currentMenuItem) &&
              widget.config.visible
            ) {
              selectedWidgets.push(widget.id);
            }
          } else {
            selectedWidgets.push(widget.id);
          }
        }
      });

      selectedWidgets.forEach(widgetId => {
        dispatch(setSelectedWidgetIds(widgetId));
      });
      setTimeout(() => {
        setIsSelecting(false);
      }, 100);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleDragSelectionMouseDown);
    document.addEventListener('mousemove', handleDragSelectionMouseMove);
    document.addEventListener('mouseup', handleDragSelectionMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleDragSelectionMouseDown);
      document.removeEventListener('mousemove', handleDragSelectionMouseMove);
      document.removeEventListener('mouseup', handleDragSelectionMouseUp);
    };
  }, [isSelecting, startPoint, selectionBox, dispatch]);

  const SelectionBox = () => (
    <>
      {isSelecting && (
        <div
          data-test-id="selection-box"
          ref={selectionBoxRef}
          style={{
            position: 'absolute',
            left: selectionBox.x,
            top: selectionBox.y,
            width: selectionBox.width,
            height: selectionBox.height,
            border: '1px dashed #000',
            backgroundColor: 'rgba(0, 0, 255, 0.1)',
            zIndex: 1000,
          }}
        />
      )}
    </>
  );
  //modifying the return statement to expose the selectionBox object for testing purposes
  return { containerRef, isSelecting, selectionBox, SelectionBox };
};

export default useDragSelection;
